/**
 * Enhanced Error Boundary Component with Sentry Integration
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and reports them to Sentry for monitoring.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../utils/logger';

// Import Sentry functions (will work after npm install)
let captureError: ((error: Error, context?: Record<string, any>) => void) | undefined;
let addSentryBreadcrumb: ((message: string, category: string, level?: any, data?: Record<string, any>) => void) | undefined;

// Dynamic import for Sentry to avoid top-level await
const initSentry = async () => {
  try {
    const sentryModule = await import('../lib/sentry');
    captureError = sentryModule.captureError;
    addSentryBreadcrumb = sentryModule.addSentryBreadcrumb;
  } catch {
    // Sentry not available, will fallback to logging only
  }
};

// Initialize Sentry when the module loads
initSentry();

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isolate?: boolean; // Whether to isolate this boundary
  name?: string; // Boundary identifier for debugging
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private retryTimeouts: NodeJS.Timeout[] = [];
  private maxRetries = 3;
  private retryDelay = 1000; // 1 second

  constructor(props: Props) {
    super(props);
    
    this.state = {
      hasError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, name = 'Unknown' } = this.props;
    const { errorId } = this.state;

    // Create detailed error context
    const errorContext = {
      boundary: name,
      errorId,
      retryCount: this.state.retryCount,
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      ...this.getPerformanceContext()
    };

    // Log error locally
    logger.error('Error boundary caught error', error, {
      component: 'ErrorBoundary',
      metadata: errorContext
    });

    // Report to Sentry if available
    if (captureError) {
      captureError(error, errorContext);
    }

    // Add breadcrumb for debugging
    if (addSentryBreadcrumb) {
      addSentryBreadcrumb(
        `Error boundary "${name}" caught error`,
        'error',
        'error',
        {
          errorId,
          errorMessage: error.message,
          componentStack: errorInfo.componentStack?.slice(0, 200) || 'Unknown' // Truncate for brevity
        }
      );
    }

    // Update state with error info
    this.setState({
      errorInfo,
      hasError: true
    });

    // Call custom error handler
    if (onError) {
      try {
        onError(error, errorInfo);
      } catch (handlerError) {
        logger.error('Error in custom error handler', handlerError as Error, {
          component: 'ErrorBoundary',
          metadata: { originalError: error.message }
        });
      }
    }

    // Auto-retry for certain types of errors
    this.scheduleRetry(error);
  }

  private getPerformanceContext(): Record<string, any> {
    const context: Record<string, any> = {};

    try {
      if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        context.pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        context.domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;
      }

      if (window.performance && 'memory' in window.performance) {
        const memory = (window.performance as any).memory;
        context.memoryUsage = {
          used: memory.usedJSHeapSize,
          total: memory.totalJSHeapSize,
          limit: memory.jsHeapSizeLimit
        };
      }

      // Add connection info
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        context.connectionType = connection?.effectiveType;
        context.connectionSpeed = connection?.downlink;
      }
    } catch (perfError) {
      // Ignore performance context errors
    }

    return context;
  }

  private scheduleRetry(error: Error): void {
    const { retryCount } = this.state;

    // Only retry for certain types of errors
    if (!this.shouldRetry(error) || retryCount >= this.maxRetries) {
      return;
    }

    const delay = this.retryDelay * Math.pow(2, retryCount); // Exponential backoff

    logger.info('Scheduling error boundary retry', {
      component: 'ErrorBoundary',
      metadata: {
        retryCount: retryCount + 1,
        delay,
        errorMessage: error.message
      }
    });

    const timeout = setTimeout(() => {
      this.retry();
    }, delay);

    this.retryTimeouts.push(timeout);
  }

  private shouldRetry(error: Error): boolean {
    const retryablePatterns = [
      /ChunkLoadError/i,
      /Loading chunk \d+ failed/i,
      /Loading CSS chunk/i,
      /Network request failed/i,
      /Failed to fetch/i
    ];

    return retryablePatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.name)
    );
  }

  private retry = (): void => {
    logger.info('Retrying after error boundary catch', {
      component: 'ErrorBoundary',
      metadata: { retryCount: this.state.retryCount + 1 }
    });

    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: undefined,
      retryCount: prevState.retryCount + 1
    }));
  };

  componentWillUnmount(): void {
    // Clean up any pending retry timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  render(): ReactNode {
    const { hasError, error, errorInfo, errorId, retryCount } = this.state;
    const { children, fallback, name = 'Unknown' } = this.props;

    if (hasError && error) {
      // Custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div style={{
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
          <div style={{
            maxWidth: '600px',
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            
            <h2 style={{
              color: '#2d3748',
              fontSize: '1.8rem',
              fontWeight: '600',
              marginBottom: '1rem'
            }}>
              Something went wrong
            </h2>
            
            <p style={{
              color: '#4a5568',
              lineHeight: '1.6',
              marginBottom: '1.5rem'
            }}>
              We're sorry, but something unexpected happened. The error has been reported 
              and our team will look into it.
            </p>

            {process.env.NODE_ENV === 'development' && (
              <details style={{
                textAlign: 'left',
                margin: '1.5rem 0',
                padding: '1rem',
                background: '#f7fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '0.5rem'
                }}>Error Details (Development)</summary>
                <div style={{ fontSize: '0.875rem', color: '#4a5568' }}>
                  <p><strong>Error ID:</strong> {errorId}</p>
                  <p><strong>Boundary:</strong> {name}</p>
                  <p><strong>Retry Count:</strong> {retryCount}</p>
                  <p><strong>Error:</strong> {error.message}</p>
                  <p><strong>Stack:</strong></p>
                  <pre style={{
                    background: '#1a202c',
                    color: '#f7fafc',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.75rem',
                    lineHeight: '1.4'
                  }}>{error.stack}</pre>
                  {errorInfo && (
                    <>
                      <p><strong>Component Stack:</strong></p>
                      <pre style={{
                        background: '#1a202c',
                        color: '#f7fafc',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.75rem',
                        lineHeight: '1.4'
                      }}>{errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              margin: '1.5rem 0'
            }}>
              <button 
                onClick={this.retry}
                disabled={retryCount >= this.maxRetries}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: retryCount >= this.maxRetries ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  background: retryCount >= this.maxRetries ? '#a0aec0' : '#4299e1',
                  color: 'white'
                }}
              >
                {retryCount >= this.maxRetries ? 'Max Retries Reached' : 'Try Again'}
              </button>
              
              <button 
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  background: '#48bb78',
                  color: 'white'
                }}
              >
                Reload Page
              </button>
            </div>

            <p style={{
              fontSize: '0.875rem',
              color: '#718096',
              marginTop: '1.5rem'
            }}>
              If this problem persists, please{' '}
              <a 
                href="mailto:support@naiamusic.com" 
                style={{
                  color: '#4299e1',
                  textDecoration: 'none'
                }}
              >
                contact our support team
              </a>{' '}
              and include the error ID: <code style={{
                background: '#edf2f7',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontFamily: 'Courier New, monospace',
                fontSize: '0.875rem'
              }}>{errorId}</code>
            </p>
          </div>


        </div>
      );
    }

    return children;
  }
}

// Higher-order component for easy wrapping
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for manual error reporting
export const useErrorHandler = () => {
  const reportError = React.useCallback((error: Error, context?: Record<string, any>) => {
    logger.error('Manual error report', error, {
      component: 'useErrorHandler',
      metadata: context
    });

    if (captureError) {
      captureError(error, context);
    }
  }, []);

  return { reportError };
};

export default ErrorBoundary; 